import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { onboardingAPI } from '../../services/api';
import LandingNavbar from '../../components/LandingNavbar';
import Step1Account from './steps/Step1Account';
import Step2LabInfo from './steps/Step2LabInfo';
import Step3Branches from './steps/Step3Branches';
import Step4Plan from './steps/Step4Plan';
import Step5Devices from './steps/Step5Devices';
import Step6Checkout from './steps/Step6Checkout';
import Step7Receipt from './steps/Step7Receipt';
import Step8Pending from './steps/Step8Pending';
import Step9Activated from './steps/Step9Activated';
import { BRAND } from '../../components/onboarding/primitives';

const STATE_KEY = 'lis_onboarding_state';
const STEP_KEY = 'lis_onboarding_step';

// Map backend "nextStep" strings → step numbers in this flow.
// Positions 5–9 shifted by +1 compared to the pre-devices layout: the new
// Step 5 (Devices) sits between Plan (4) and Checkout (now 6).
const STEP_MAP = {
  labInfo: 2,
  branches: 3,
  plan: 4,
  devices: 5,
  checkout: 6,
  receipt: 7,
  pending: 8,
  rejected: 8,
  activated: 9,
};

const defaultState = () => ({
  s1: {},
  s2: {},
  s3: { branches: [{
    id: 1, name: '', city: '', address: '',
    supervisor_name: '', supervisor_mobile: '', supervisor_email: '',
  }] },
  s4: { plan: null, cycle: 'yearly' },
  s5: { deviceCount: null }, // devices
  s6: {}, // checkout
  s7: {}, // receipt
});

function loadState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    return { ...defaultState(), ...parsed };
  } catch {
    return defaultState();
  }
}

function loadInitialStep() {
  const raw = localStorage.getItem(STEP_KEY);
  const n = parseInt(raw, 10);
  if (!isNaN(n) && n >= 1 && n <= 9) return n;
  return 1;
}

// Placeholder for steps that aren't built yet (5-8). Real components will
// replace these in a follow-up.
function PlaceholderStep({ step, goBack }) {
  return (
    <div style={{
      minHeight: '100vh', background: BRAND.surface,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: 'inherit',
    }}>
      <div style={{
        maxWidth: 520, width: '100%',
        background: '#fff', border: `1px solid ${BRAND.border}`,
        borderRadius: 20, padding: 40, textAlign: 'center',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: BRAND.text }}>
          الخطوة {step} قيد البناء
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: BRAND.muted, lineHeight: 1.7 }}>
          سيتم إضافة هذه الخطوة قريباً. في الوقت الحالي يمكنك الرجوع وتعديل بياناتك.
        </p>
        <button
          onClick={goBack}
          style={{
            background: 'transparent', border: `1px solid ${BRAND.border}`,
            borderRadius: 12, padding: '10px 20px', fontSize: 14,
            color: BRAND.text, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          رجوع
        </button>
      </div>
    </div>
  );
}

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const [state, setStateRaw] = useState(loadState);
  const [current, setCurrent] = useState(loadInitialStep);
  const [statusChecked, setStatusChecked] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const historyRef = useRef([]);

  // Persist state to localStorage on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {
      /* ignore quota errors */
    }
  }, [state]);

  // Persist current step.
  useEffect(() => {
    try {
      localStorage.setItem(STEP_KEY, String(current));
    } catch {
      /* ignore */
    }
  }, [current]);

  // On mount, consult backend for nextStep — but only if we have auth token.
  // Unauthenticated users start at step 1.
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusChecked(true);
        return;
      }
      try {
        const res = await onboardingAPI.getStatus();
        if (cancelled) return;
        // successResponse wraps payload under .data
        const payload = res?.data?.data || res?.data || {};
        const nextStep = payload.nextStep;

        // Hydrate local state with whatever the backend knows about the
        // tenant so individual steps can show sensible defaults after a
        // refresh (name pre-filled on Step 2, branch count on Step 3, etc.).
        if (payload.snapshot || payload.activePaymentOrderId || payload.accountStatus) {
          setStateRaw((prev) => ({
            ...prev,
            backendSnapshot: payload.snapshot || null,
            accountStatus: payload.accountStatus || null,
            activePaymentOrderId: payload.activePaymentOrderId || null,
            rejectionReason: payload.rejectionReason || null,
          }));
        }

        if (!nextStep) {
          setStatusChecked(true);
          return;
        }
        if (nextStep === 'dashboard') {
          // Clear stale onboarding scratchpad before entering the dashboard.
          try {
            localStorage.removeItem(STATE_KEY);
            localStorage.removeItem(STEP_KEY);
          } catch { /* ignore */ }
          navigate('/', { replace: true });
          return;
        }
        const mapped = STEP_MAP[nextStep];
        if (mapped) {
          // Backend is authoritative — always jump to the step it reports,
          // even backwards. Users who logged out mid-flow and came back
          // (or manually cleared localStorage) deserve to land exactly
          // where they left off, not a stale cached step that might point
          // to a screen the server can no longer fulfill (e.g. Step 5
          // Checkout without an active draft).
          setCurrent(mapped);
          historyRef.current = [];
        }
      } catch (err) {
        // getStatus failed — we're authenticated (we checked `token` above)
        // but we can't determine the real step. Rather than silently dropping
        // the user on step 1 with an empty registration form — which will
        // then 409 when they try to submit because their email is already
        // taken — surface an explicit error screen with a logout escape.
        // eslint-disable-next-line no-console
        console.warn('[OnboardingFlow] getStatus failed:', err?.response?.status || err?.message);
        if (!cancelled) setStatusError(true);
      } finally {
        if (!cancelled) setStatusChecked(true);
      }
    };
    fetchStatus();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setState = useCallback((updater) => {
    setStateRaw((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const goToStep = useCallback((n) => {
    if (n === 'dashboard') {
      // Clear onboarding state and navigate home.
      try {
        localStorage.removeItem(STATE_KEY);
        localStorage.removeItem(STEP_KEY);
      } catch { /* ignore */ }
      navigate('/', { replace: true });
      return;
    }
    setCurrent((prev) => {
      // Forward navigation: record where we came from so goBack can pop to it.
      // Backward navigation (n <= prev): treat as a jump *back* so we don't
      // pollute the history stack — pop any entries >= n off the top, then
      // just land on n. Without this, callers that use goToStep(lower) to
      // emulate "back" cause goBack from the target to re-enter the future.
      if (n > prev) {
        historyRef.current.push(prev);
      } else if (n < prev) {
        while (historyRef.current.length > 0
          && historyRef.current[historyRef.current.length - 1] >= n) {
          historyRef.current.pop();
        }
      }
      return n;
    });
  }, [navigate]);

  const goBack = useCallback(() => {
    setCurrent((prev) => {
      // Pop until we find an entry strictly before `prev`. Any stale entries
      // equal to or greater than the current step are leftovers from a
      // forward→back→forward re-entry and must be discarded.
      while (historyRef.current.length > 0
        && historyRef.current[historyRef.current.length - 1] >= prev) {
        historyRef.current.pop();
      }
      const last = historyRef.current.pop();
      if (typeof last === 'number') return last;
      // Fallback: decrement by one, floor at 1.
      return Math.max(1, prev - 1);
    });
  }, []);

  // While we're probing /onboarding/status, show a minimal loading frame
  // to avoid rendering then jumping.
  if (!statusChecked) {
    return (
      <div dir="rtl" style={{
        minHeight: '100vh', background: BRAND.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', color: BRAND.muted, fontSize: 14,
      }}>
        جارٍ التحميل...
      </div>
    );
  }

  // If the status probe failed while authenticated, never drop the user on
  // a blank step 1 — that lures them into re-registering and hitting a 409.
  // Show a recovery screen with a logout escape.
  if (statusError) {
    return (
      <div dir="rtl" style={{
        minHeight: '100vh', background: BRAND.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', padding: 24,
      }}>
        <div style={{
          maxWidth: 480, width: '100%', background: '#fff',
          border: `1px solid ${BRAND.border}`, borderRadius: 20,
          padding: 36, textAlign: 'center',
        }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: BRAND.text }}>
            تعذّر تحميل حالة التسجيل
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: BRAND.muted, lineHeight: 1.7 }}>
            صار خطأ أثناء جلب بيانات حسابك. حاول تحديث الصفحة، وإن استمرت المشكلة
            سجّل خروج وأعد الدخول.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: BRAND.green, color: '#fff', border: 'none',
                borderRadius: 12, padding: '10px 22px', fontSize: 14,
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              }}
            >
              إعادة المحاولة
            </button>
            <button
              onClick={() => {
                try {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  localStorage.removeItem('tenant');
                  localStorage.removeItem(STATE_KEY);
                  localStorage.removeItem(STEP_KEY);
                } catch { /* ignore */ }
                navigate('/staff-login', { replace: true });
              }}
              style={{
                background: 'transparent', color: BRAND.text,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12, padding: '10px 22px', fontSize: 14,
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
              }}
            >
              تسجيل خروج
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stepProps = { state, setState, goToStep, goBack };

  // Rejection reason for Step 7. Populated from the active order when the
  // backend status comes back as 'rejected'.
  const rejectionReason = state.activeOrder?.rejectionReason || null;
  const isRejected = state.activeOrder?.status === 'rejected';

  let StepComponent;
  switch (current) {
    case 1: StepComponent = <Step1Account {...stepProps} />; break;
    case 2: StepComponent = <Step2LabInfo {...stepProps} />; break;
    case 3: StepComponent = <Step3Branches {...stepProps} />; break;
    case 4: StepComponent = <Step4Plan {...stepProps} />; break;
    case 5: StepComponent = <Step5Devices {...stepProps} />; break;
    case 6: StepComponent = <Step6Checkout {...stepProps} />; break;
    case 7: StepComponent = <Step7Receipt {...stepProps} />; break;
    case 8: StepComponent = <Step8Pending state={state} rejected={isRejected} rejectionReason={rejectionReason} goToStep={goToStep} />; break;
    case 9: StepComponent = <Step9Activated />; break;
    default: StepComponent = <PlaceholderStep step={current} goBack={goBack} />;
  }

  return (
    <div dir="rtl" style={{ fontFamily: 'inherit', minHeight: '100vh' }}>
      <LandingNavbar />
      {StepComponent}
    </div>
  );
}

// Named exports for advanced consumers / tests.
export { STATE_KEY, STEP_KEY, STEP_MAP };
